import { LIFECYCLE_STATUS_VALUES, LifecycleStatus } from '@clockwise/shared';
import { z } from 'zod';

const writableStatus = z.union([
  z.literal(LifecycleStatus.Active),
  z.literal(LifecycleStatus.Inactive),
]);

const lifecycleStatus = z
  .number()
  .int()
  .refine((n): n is LifecycleStatus => (LIFECYCLE_STATUS_VALUES as readonly number[]).includes(n));

const visibleToServiceRoleIdsInput = z.array(z.string().uuid()).max(50).optional();
// Server always returns an array (never null/undefined); inputs are optional.
const visibleToServiceRoleIdsResponse = z.array(z.string().uuid());

export const ProductSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  // NULL for a global/standard activity; a customer id for a customer-specific
  // ("custom") activity that only that customer can log against.
  customerId: z.string().uuid().nullable(),
  // Optional, non-unique billing labels.
  retainerCode: z.string().nullable(),
  tmCode: z.string().nullable(),
  /** The chart-of-accounts rows behind the two codes. */
  retainerAccountId: z.string().nullable(),
  tmAccountId: z.string().nullable(),
  name: z.string(),
  description: z.string().nullable(),
  // PRIMARY role — what shows in the autocomplete badge.
  serviceRoleId: z.string().uuid(),
  // ADDITIONAL roles with visibility. Primary is implicitly included;
  // callers should treat effective visibility as
  //   {serviceRoleId} ∪ visibleToServiceRoleIds.
  visibleToServiceRoleIds: visibleToServiceRoleIdsResponse,
  internal: z.boolean(),
  // Entries logged against this activity never count toward — and can never
  // individually push over — a customer budget's consumption.
  excludeFromBudget: z.boolean(),
  status: lifecycleStatus,
  createdBy: z.string().uuid(),
  updatedBy: z.string().uuid(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Product = z.infer<typeof ProductSchema>;

const ProductCommon = z.object({
  // Optional free-form labels. Empty string or null normalize to "no code"
  // on the server; omitting the key leaves the existing value alone on patch.
  retainerCode: z.string().max(64).nullable().optional(),
  tmCode: z.string().max(64).nullable().optional(),
  /** When given, wins over the code and sets it from the account. */
  retainerAccountId: z.string().uuid().nullable().optional(),
  tmAccountId: z.string().uuid().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  serviceRoleId: z.string().uuid(),
  visibleToServiceRoleIds: visibleToServiceRoleIdsInput,
  internal: z.boolean().optional(),
  excludeFromBudget: z.boolean().optional(),
});

export const CreateProductInput = ProductCommon;
export type CreateProductInput = z.infer<typeof CreateProductInput>;

export const PatchProductInput = ProductCommon.partial();
export type PatchProductInput = z.infer<typeof PatchProductInput>;

export const SetProductStatusInput = z.object({ status: writableStatus });
export type SetProductStatusInput = z.infer<typeof SetProductStatusInput>;

export interface ListProductsQuery {
  q?: string;
  retainerCode?: string;
  tmCode?: string;
  /** Plain equality on the primary role. */
  serviceRoleId?: string;
  /** Effective-visibility filter: match products where this role is the
   *  primary OR is in visibleToServiceRoleIds. */
  accessibleByRoleId?: string;
  /** Effective-visibility filter, CSV form: ANY of these roles is the
   *  primary OR in visibleToServiceRoleIds. */
  accessibleByRoleIds?: string[];
  /** Customer scope. When set, the list returns global activities PLUS that
   *  customer's custom activities (and excludes other customers'). When
   *  omitted, only global activities are returned. */
  customerId?: string;
  status?: LifecycleStatus;
  internal?: boolean;
  excludeFromBudget?: boolean;
  sortField?: 'retainerCode' | 'tmCode' | 'name' | 'status' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/** Result of POST /api/activities/import — one entry per row in the
 *  uploaded file (in the same order). `outcome` reflects whether the
 *  row was created (blank Internal ID), updated (matched by Internal ID),
 *  or rejected. */
export type ImportProductRowOutcome = 'created' | 'updated' | 'failed';

export interface ImportProductsRow {
  rowNumber: number;
  /** The row's Internal ID (the import identity), or null for a create row
   *  or a row whose ID cell was blank/malformed. */
  id: string | null;
  outcome: ImportProductRowOutcome;
  errors?: string[];
}

export interface ImportProductsResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportProductsRow[];
}
